import React, { useEffect, useRef } from 'react';

// Plum noir — sanctuary plum, complementary to gold accents.
const HERO_BG = '#1D1116';

export default function AnimatedHero({ onNavigate }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    /* Load Cormorant Garamond from Google Fonts if not already present.
       Self-contained — doesn't rely on tokens.css. */
    const FONT_LINK_ID = 'cleo-hero-cormorant';
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement('link');
      link.id = FONT_LINK_ID;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap';
      document.head.appendChild(link);
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    /* Seeded random so constellation looks the same on every reload */
    const seedRng = (seed) => {
      let r = seed;
      return () => {
        r = (r * 9301 + 49297) % 233280;
        return r / 233280;
      };
    };

    /* Constellation stars — fixed x position, drifting y, slight parallax */
    const starRng = seedRng(7);
    const stars = [];
    for (let i = 0; i < 38; i++) {
      stars.push({
        xRatio: starRng(),
        yRatio: starRng(),
        size: starRng() * 1.1 + 0.5,
        opacity: starRng() * 0.35 + 0.22,
        /* Drift speed varies per star for parallax: 0.18–0.28 px/frame at 60fps
           = roughly 11–17 px/sec. A star traverses the hero in ~35–50 sec. */
        driftSpeed: starRng() * 0.10 + 0.18,
      });
    }

    /* Dust particles — drift upward (opposite direction to stars for atmosphere depth) */
    const dustRng = seedRng(42);
    const dust = [];
    for (let i = 0; i < 42; i++) {
      dust.push({
        xRatio: dustRng(),
        yRatio: dustRng(),
        size: dustRng() * 1.4 + 0.5,
        opacity: dustRng() * 0.4 + 0.2,
        /* Slow lazy upward drift: 0.08–0.15 px/frame = ~5–9 px/sec */
        driftSpeedY: -(dustRng() * 0.07 + 0.08),
        /* Subtle horizontal sway, varies per particle */
        driftSpeedX: (dustRng() - 0.5) * 0.04,
      });
    }

    let bgCanvas = null;
    let animationId = null;

    /* Build the static background composition off-screen.
       Plum base + plum-toned gradients (lighter plum for lit zones, deeper plum for shadows).
       Gold is reserved for foreground accents (hairlines, rule, stars, dust) — never as wash. */
    const buildBackground = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const dpr = 2;

      const off = document.createElement('canvas');
      off.width = w * dpr;
      off.height = h * dpr;
      const oc = off.getContext('2d');
      oc.scale(dpr, dpr);

      /* PLUM BASE */
      oc.fillStyle = HERO_BG;
      oc.fillRect(0, 0, w, h);

      /* Lit centre — softly LIGHTER plum from the centre outward.
         This reads as "warm interior light" without using gold tones.
         #2A1820 is the lifted plum (matches the nav). */
      const centreGlow = oc.createRadialGradient(
        w * 0.5, h * 0.4, 0,
        w * 0.5, h * 0.4, Math.max(w, h) * 0.7
      );
      centreGlow.addColorStop(0, 'rgba(58, 33, 44, 0.55)');     /* even lighter plum at the centre */
      centreGlow.addColorStop(0.4, 'rgba(42, 24, 32, 0.30)');   /* lifted plum mid-falloff */
      centreGlow.addColorStop(1, 'rgba(29, 17, 22, 0)');        /* dissolve to base plum */
      oc.fillStyle = centreGlow;
      oc.fillRect(0, 0, w, h);

      /* Subtle horizon glow — gold tint only at the very bottom edge.
         Limited to bottom 25% so it doesn't wash the headline area. */
      const horizonGrad = oc.createRadialGradient(
        w * 0.5, h * 1.0, 0,
        w * 0.5, h * 1.0, w * 0.5
      );
      horizonGrad.addColorStop(0, 'rgba(224, 198, 133, 0.06)');
      horizonGrad.addColorStop(0.4, 'rgba(224, 198, 133, 0.02)');
      horizonGrad.addColorStop(1, 'rgba(224, 198, 133, 0)');
      oc.fillStyle = horizonGrad;
      oc.fillRect(0, 0, w, h);

      /* Two vertical hairlines — gold accent at margins */
      const drawHairline = (x, yStart, yEnd) => {
        const grad = oc.createLinearGradient(x, yStart, x, yEnd);
        grad.addColorStop(0, 'rgba(201, 169, 97, 0)');
        grad.addColorStop(0.5, 'rgba(201, 169, 97, 0.45)');
        grad.addColorStop(1, 'rgba(201, 169, 97, 0)');
        oc.strokeStyle = grad;
        oc.lineWidth = 1;
        oc.beginPath();
        oc.moveTo(x, yStart);
        oc.lineTo(x, yEnd);
        oc.stroke();
      };
      drawHairline(w * 0.085, h * 0.28, h * 0.72);
      drawHairline(w * 0.915, h * 0.18, h * 0.62);

      /* Horizontal magazine rule at 78% height */
      const hRuleY = h * 0.78;
      const hRuleGrad = oc.createLinearGradient(0, hRuleY, w, hRuleY);
      hRuleGrad.addColorStop(0, 'rgba(201, 169, 97, 0)');
      hRuleGrad.addColorStop(0.3, 'rgba(201, 169, 97, 0.18)');
      hRuleGrad.addColorStop(0.7, 'rgba(201, 169, 97, 0.18)');
      hRuleGrad.addColorStop(1, 'rgba(201, 169, 97, 0)');
      oc.strokeStyle = hRuleGrad;
      oc.lineWidth = 1;
      oc.beginPath();
      oc.moveTo(0, hRuleY);
      oc.lineTo(w, hRuleY);
      oc.stroke();

      /* Vignette — corner darkening in PLUM tones, not pure black.
         Pure black vignette on plum reads as "brown shadow" because
         the eye can't distinguish near-black from very-dark-brown.
         Using a deeper plum keeps the colour identity in the corners. */
      const vignette = oc.createRadialGradient(
        w * 0.5, h * 0.5, w * 0.4,
        w * 0.5, h * 0.5, w * 0.95
      );
      vignette.addColorStop(0, 'rgba(15, 8, 12, 0)');           /* deep plum-black, transparent at centre */
      vignette.addColorStop(1, 'rgba(15, 8, 12, 0.55)');        /* deep plum-black opaque at corners */
      oc.fillStyle = vignette;
      oc.fillRect(0, 0, w, h);

      /* Grain — composited non-destructively from a separate transparent canvas.
         Cool/neutral tint so it doesn't push the plum toward brown. */
      const grainCanvas = document.createElement('canvas');
      grainCanvas.width = w * dpr;
      grainCanvas.height = h * dpr;
      const gc = grainCanvas.getContext('2d');
      const grainData = gc.createImageData(w * dpr, h * dpr);
      const data = grainData.data;
      for (let i = 0; i < data.length; i += 4) {
        const n = Math.random();
        if (n > 0.93) {
          const intensity = (n - 0.93) * 14 * 255;
          /* Neutral grey-grain — equal RGB, no warm shift */
          data[i] = intensity * 0.55;
          data[i + 1] = intensity * 0.52;
          data[i + 2] = intensity * 0.52;
          data[i + 3] = intensity * 0.12;
        }
      }
      gc.putImageData(grainData, 0, 0);
      oc.drawImage(grainCanvas, 0, 0, w, h);

      bgCanvas = off;
    };

    /* Set up main canvas dimensions and build background */
    const setupCanvas = () => {
      const dpr = 2;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildBackground();
    };

    /* Render frame — draw cached background, then drifting stars + connections */
    const render = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;

      ctx.clearRect(0, 0, w, h);

      /* Cached static background */
      if (bgCanvas) {
        ctx.drawImage(bgCanvas, 0, 0, w, h);
      }

      /* Advance dust drift and draw */
      dust.forEach(p => {
        p.xRatio += p.driftSpeedX / w;
        p.yRatio += p.driftSpeedY / h;
        /* Wrap top to bottom (dust drifts upward) */
        if (p.yRatio < -0.05) {
          p.yRatio = 1.05;
          p.xRatio = Math.random();
        }
        /* Wrap horizontal sway */
        if (p.xRatio < -0.05) p.xRatio = 1.05;
        if (p.xRatio > 1.05) p.xRatio = -0.05;

        const px = p.xRatio * w;
        const py = p.yRatio * h;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201, 169, 97, ${p.opacity})`;
        ctx.fill();
      });

      /* Advance star drift and compute current positions */
      const starPositions = stars.map(s => {
        s.yRatio += s.driftSpeed / h;
        /* Wrap around: when a star drifts past the bottom, recycle to top */
        if (s.yRatio > 1.05) {
          s.yRatio = -0.05;
          /* Re-randomize x slightly to avoid columnar patterns */
          s.xRatio = (s.xRatio + 0.13) % 1;
        }
        return {
          x: s.xRatio * w,
          y: s.yRatio * h,
          size: s.size,
          opacity: s.opacity,
        };
      });

      /* Faint connection lines between nearby stars */
      const maxStarDist = Math.min(w, h) * 0.16;
      for (let i = 0; i < starPositions.length; i++) {
        for (let j = i + 1; j < starPositions.length; j++) {
          const dx = starPositions[i].x - starPositions[j].x;
          const dy = starPositions[i].y - starPositions[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxStarDist) {
            const fade = 1 - dist / maxStarDist;
            ctx.strokeStyle = `rgba(201, 169, 97, ${fade * 0.06})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(starPositions[i].x, starPositions[i].y);
            ctx.lineTo(starPositions[j].x, starPositions[j].y);
            ctx.stroke();
          }
        }
      }

      /* Stars */
      starPositions.forEach(s => {
        if (s.y < -5 || s.y > h + 5) return;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201, 169, 97, ${s.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(render);
    };

    setupCanvas();
    render();

    const onResize = () => {
      setupCanvas();
    };
    window.addEventListener('resize', onResize);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div style={{
      position: 'relative',
      background: HERO_BG,
      /* nav is 108px tall; hero fills the remainder of the first viewport */
      height: 'calc(100vh - 108px)',
      minHeight: 620,
      maxHeight: 920,
      overflow: 'hidden',
    }}>
      {/* Canvas — static composition + drifting constellation */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%', height: '100%',
        }}
      />

      {/* Content overlay */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 32px 60px',
        textAlign: 'center',
      }}>
        {/* Premise — italic gold, 24-36px (replaces eyebrow per doc-locked hook spec).
            No trailing period per doc — sits as a sentence fragment. */}
        <p style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(20px, 2.4vw, 32px)',
          fontStyle: 'italic',
          fontWeight: 400,
          color: 'rgba(224, 198, 133, 0.88)',
          lineHeight: 1.35,
          letterSpacing: '-0.005em',
          margin: '0 0 28px',
          animation: 'fadeUp 1.2s ease forwards',
          opacity: 0,
        }}>
          Something has shifted
        </p>

        {/* Title — Cormorant 44-72px. Roman "The secret is" + italic span
            "feeling like yourself again" at 0.9em. No trailing period per
            doc. GO for roman + GO2 italic, adjusted from doc's BR roman
            for dark-plum hero ground (BR #9A7430 reads muddy on plum). */}
        <h1 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(42px, 6.4vw, 84px)',
          fontWeight: 400,
          color: '#C9A961',
          lineHeight: 1.12,
          letterSpacing: '-0.008em',
          maxWidth: 1080,
          marginBottom: 24,
          animation: 'fadeUp 1.2s ease 0.3s forwards',
          opacity: 0,
        }}>
          The secret is{' '}
          <span style={{ fontStyle: 'italic', color: '#E0C685', fontSize: '0.9em' }}>
            feeling like yourself again
          </span>
        </h1>

        {/* Geometric divider */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          margin: '28px 0',
          animation: 'fadeUp 1.2s ease 0.5s forwards',
          opacity: 0,
        }}>
          <div style={{ width: 72, height: '0.5px', background: '#C9A961', opacity: 0.32 }} />
          <div style={{ width: 8, height: 8, background: '#C9A961', transform: 'rotate(45deg)', opacity: 0.45 }} />
          <div style={{ width: 72, height: '0.5px', background: '#C9A961', opacity: 0.32 }} />
        </div>

        {/* Subtitle — doc-locked. Italic gold, 22-32px. */}
        <p style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(20px, 2.3vw, 30px)',
          fontStyle: 'italic',
          fontWeight: 400,
          color: 'rgba(224, 198, 133, 0.88)',
          lineHeight: 1.4,
          maxWidth: 760,
          marginBottom: 44,
          animation: 'fadeUp 1.2s ease 0.7s forwards',
          opacity: 0,
        }}>
          Comprehensive clinical care, supported by modern rituals, for women navigating perimenopause and menopause.
        </p>

        {/* Primary CTA — ONE button per doc § "What does NOT appear":
            secondary "Our approach" CTA removed. One CTA only in hero. */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          animation: 'fadeUp 1.2s ease 0.9s forwards',
          opacity: 0,
        }}>
          <button
            onClick={() => onNavigate('intake')}
            style={{
              fontFamily: "system-ui, -apple-system, sans-serif",
              fontSize: 12, fontWeight: 600,
              letterSpacing: '0.20em', textTransform: 'uppercase',
              padding: '16px 36px',
              background: '#C9A961', color: HERO_BG,
              border: 'none', borderRadius: 3, cursor: 'pointer',
              transition: 'background 0.25s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#E0C685'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#C9A961'; }}
          >Begin your health conversation</button>
        </div>

        {/* Credibility strip — Cinzel small caps, GO at 65% opacity, ~13px,
            ls 0.32em. Sits ~24px below CTA. Reads as part of the hook block
            per doc layout note. Doc-locked May 2026: no "Melbourne" (platform
            is Australia-wide via telehealth); "perimenopause and menopause"
            doubles as SEO signal. */}
        <div style={{
          fontFamily: "'Cinzel', 'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(11px, 1vw, 13px)',
          fontWeight: 500,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'rgba(201, 169, 97, 0.65)',
          marginTop: 24,
          animation: 'fadeUp 1.2s ease 1.1s forwards',
          opacity: 0,
        }}>
          GP-led women&apos;s health for perimenopause and menopause &middot; By appointment
        </div>

        {/* Scroll indicator */}
        <div style={{
          position: 'absolute', bottom: 32, left: '50%',
          transform: 'translateX(-50%)',
          animation: 'bounce 2s ease-in-out infinite',
        }}>
          <div style={{
            width: 22, height: 36, borderRadius: 11,
            border: '1px solid rgba(201, 169, 97, 0.25)',
            display: 'flex', justifyContent: 'center', paddingTop: 7,
          }}>
            <div style={{
              width: 3, height: 9, borderRadius: 2,
              background: 'rgba(201, 169, 97, 0.4)',
              animation: 'scrollDot 2s ease-in-out infinite',
            }} />
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(6px); }
        }
        @keyframes scrollDot {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 0.7; transform: translateY(6px); }
        }
      `}</style>
    </div>
  );
}
